import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

import { SESSION_TENANT_CONTEXT_KEY } from './storage-keys';

export interface TenantContext {
  tenantId: string;
  envId: string;
  userId: string;
  operatorEmail: string;
  tenantDisplayName: string;
}

/**
 * TenantContextService — single source of truth for the authenticated
 * operator's Tenant/Environment context. Populated from access_token +
 * id_token claims after sign-in (tenant_id + env_id from access_token per
 * CLAUDE.md D17; sub + email from id_token) and from GET /admin/tenant
 * (DisplayName).
 *
 * Persistence: the context is mirrored to **sessionStorage** so a page
 * refresh keeps the sidebar header rendering the right tenant identity
 * without an extra round-trip. AuthService's logout() clears this key
 * alongside the token key — see AuthService.TENANT_CONTEXT_STORAGE_KEY.
 *
 * Components subscribe to `context$` and render reactively. The service
 * publishes `null` while no operator is signed in.
 */
@Injectable({ providedIn: 'root' })
export class TenantContextService {
  private readonly subject = new BehaviorSubject<TenantContext | null>(this.rehydrate());
  // N8 (Opus RP7 2026-05-23): buffer a pending display-name update that
  // arrived BEFORE the context was initialised. The prior setTenantDisplayName
  // silently no-op'd when `subject.value` was null — under a microtask race
  // between sign-in `set()` and a concurrent `GET /admin/tenant` from the
  // sidebar, the display-name update could be lost forever, leaving the
  // sidebar header showing email-as-display-name until the next page load.
  private pendingDisplayName: string | null = null;

  context$: Observable<TenantContext | null> = this.subject.asObservable();

  current(): TenantContext | null {
    return this.subject.value;
  }

  set(context: TenantContext): void {
    const final =
      this.pendingDisplayName !== null
        ? { ...context, tenantDisplayName: this.pendingDisplayName }
        : context;
    this.pendingDisplayName = null;
    this.persist(final);
    this.subject.next(final);
  }

  /**
   * Update the Tenant display name without disturbing the rest of the
   * context. Called after `GET /admin/tenant` returns the canonical record
   * (the id_token's display claim is stale post-PATCH so we re-source).
   *
   * N8 — if the context isn't initialised yet, buffer the display name
   * so the next `set()` applies it instead of silently dropping the update.
   */
  setTenantDisplayName(displayName: string): void {
    const current = this.subject.value;
    if (!current) {
      this.pendingDisplayName = displayName;
      return;
    }
    const updated = { ...current, tenantDisplayName: displayName };
    this.persist(updated);
    this.subject.next(updated);
  }

  clear(): void {
    try {
      sessionStorage.removeItem(SESSION_TENANT_CONTEXT_KEY);
    } catch {
      // sessionStorage may be unavailable; best-effort.
    }
    this.subject.next(null);
    this.pendingDisplayName = null;
  }

  private persist(ctx: TenantContext): void {
    try {
      sessionStorage.setItem(SESSION_TENANT_CONTEXT_KEY, JSON.stringify(ctx));
    } catch {
      // best-effort; UX downgrades to "refresh loses sidebar identity"
    }
  }

  /**
   * Restore the tenant context from sessionStorage on service construction.
   * Validates the shape defensively — a malformed payload (truncated write,
   * tampering) is treated as no context rather than partial state.
   */
  private rehydrate(): TenantContext | null {
    try {
      const raw = sessionStorage.getItem(SESSION_TENANT_CONTEXT_KEY);
      if (raw === null) {
        return null;
      }
      const parsed = JSON.parse(raw) as Partial<TenantContext>;
      if (
        typeof parsed.tenantId !== 'string' ||
        typeof parsed.envId !== 'string' ||
        typeof parsed.userId !== 'string' ||
        typeof parsed.operatorEmail !== 'string' ||
        typeof parsed.tenantDisplayName !== 'string'
      ) {
        return null;
      }
      return {
        tenantId: parsed.tenantId,
        envId: parsed.envId,
        userId: parsed.userId,
        operatorEmail: parsed.operatorEmail,
        tenantDisplayName: parsed.tenantDisplayName,
      };
    } catch {
      return null;
    }
  }
}
